import { ReactNode } from 'react';

interface AquariumTankProps {
  children: ReactNode;
  waterHeight: number;
  waterColor: string;
}

export const AquariumTank = ({ children, waterHeight, waterColor }: AquariumTankProps) => {
  return (
    <div className="relative w-full max-w-2xl mx-auto">
      <svg
        viewBox="0 0 600 500"
        className="w-full h-auto drop-shadow-2xl"
        style={{ filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.15))' }}
      >
        <defs>
          <linearGradient id="waterGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={waterColor} stopOpacity="0.85" />
            <stop offset="50%" stopColor={waterColor} stopOpacity="0.90" />
            <stop offset="100%" stopColor={waterColor} stopOpacity="0.95" />
          </linearGradient>

          <linearGradient id="glassShine" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.05" />
            <stop offset="15%" stopColor="#ffffff" stopOpacity="0.2" />
            <stop offset="20%" stopColor="#ffffff" stopOpacity="0.05" />
            <stop offset="80%" stopColor="#ffffff" stopOpacity="0.05" />
            <stop offset="85%" stopColor="#ffffff" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
          </linearGradient>

          <linearGradient id="ssGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#1e293b" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#1e293b" stopOpacity="0.15" />
          </linearGradient>

          <filter id="blur">
            <feGaussianBlur in="SourceGraphic" stdDeviation="1" />
          </filter>

          <clipPath id="waveClip">
            <path d="M100,0 L500,0 L500,500 L100,500 Z">
              <animate
                attributeName="d"
                values="
                  M100,0 Q150,-12 200,0 T300,0 T400,0 T500,0 L500,500 L100,500 Z;
                  M100,0 Q150,12 200,0 T300,0 T400,0 T500,0 L500,500 L100,500 Z;
                  M100,0 Q150,-12 200,0 T300,0 T400,0 T500,0 L500,500 L100,500 Z
                "
                dur="3s"
                repeatCount="indefinite"
              />
            </path>
          </clipPath>

          <pattern id="bubbles" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
            <circle cx="20" cy="80" r="2" fill="white" opacity="0.2">
              <animate attributeName="cy" from="80" to="-10" dur="4s" repeatCount="indefinite" />
            </circle>
            <circle cx="60" cy="90" r="1.5" fill="white" opacity="0.3">
              <animate attributeName="cy" from="90" to="-10" dur="5s" repeatCount="indefinite" />
            </circle>
            <circle cx="80" cy="70" r="1" fill="white" opacity="0.25">
              <animate attributeName="cy" from="70" to="-10" dur="6s" repeatCount="indefinite" />
            </circle>
          </pattern>

          <clipPath id="tankClip">
            <rect x="100" y="80" width="400" height="340" rx="8" />
          </clipPath>
        </defs>

        <g clipPath="url(#tankClip)">
          <rect
            x="100"
            y={80 + (340 * (100 - 25) / 100)}
            width="400"
            height={340 * 0.25}
            fill="url(#ssGradient)"
          />

          <g clipPath="url(#waveClip)">
            <rect
              x="100"
              y={80 + (340 * (100 - waterHeight) / 100)}
              width="400"
              height={340 * waterHeight / 100}
              fill="url(#waterGradient)"
            />

            <rect
              x="100"
              y={80 + (340 * (100 - waterHeight) / 100)}
              width="400"
              height={340 * waterHeight / 100}
              fill="url(#bubbles)"
              opacity="0.6"
            />
          </g>

          <path
            d={`M100,${80 + (340 * (100 - waterHeight) / 100)} L500,${80 + (340 * (100 - waterHeight) / 100)}`}
            stroke="white"
            strokeWidth="2"
            opacity="0.4"
            fill="none"
          >
            <animate
              attributeName="d"
              values={`
                M100,${80 + (340 * (100 - waterHeight) / 100)} Q150,${80 + (340 * (100 - waterHeight) / 100) - 8} 200,${80 + (340 * (100 - waterHeight) / 100)} T300,${80 + (340 * (100 - waterHeight) / 100)} T400,${80 + (340 * (100 - waterHeight) / 100)} T500,${80 + (340 * (100 - waterHeight) / 100)};
                M100,${80 + (340 * (100 - waterHeight) / 100)} Q150,${80 + (340 * (100 - waterHeight) / 100) + 8} 200,${80 + (340 * (100 - waterHeight) / 100)} T300,${80 + (340 * (100 - waterHeight) / 100)} T400,${80 + (340 * (100 - waterHeight) / 100)} T500,${80 + (340 * (100 - waterHeight) / 100)};
                M100,${80 + (340 * (100 - waterHeight) / 100)} Q150,${80 + (340 * (100 - waterHeight) / 100) - 8} 200,${80 + (340 * (100 - waterHeight) / 100)} T300,${80 + (340 * (100 - waterHeight) / 100)} T400,${80 + (340 * (100 - waterHeight) / 100)} T500,${80 + (340 * (100 - waterHeight) / 100)}
              `}
              dur="2.5s"
              repeatCount="indefinite"
            />
          </path>

          <path
            d={`M100,${80 + (340 * (100 - waterHeight) / 100)} L500,${80 + (340 * (100 - waterHeight) / 100)}`}
            stroke="white"
            strokeWidth="1.5"
            opacity="0.25"
            fill="none"
          >
            <animate
              attributeName="d"
              values={`
                M100,${80 + (340 * (100 - waterHeight) / 100)} Q175,${80 + (340 * (100 - waterHeight) / 100) + 6} 250,${80 + (340 * (100 - waterHeight) / 100)} T400,${80 + (340 * (100 - waterHeight) / 100)} T500,${80 + (340 * (100 - waterHeight) / 100)};
                M100,${80 + (340 * (100 - waterHeight) / 100)} Q175,${80 + (340 * (100 - waterHeight) / 100) - 6} 250,${80 + (340 * (100 - waterHeight) / 100)} T400,${80 + (340 * (100 - waterHeight) / 100)} T500,${80 + (340 * (100 - waterHeight) / 100)};
                M100,${80 + (340 * (100 - waterHeight) / 100)} Q175,${80 + (340 * (100 - waterHeight) / 100) + 6} 250,${80 + (340 * (100 - waterHeight) / 100)} T400,${80 + (340 * (100 - waterHeight) / 100)} T500,${80 + (340 * (100 - waterHeight) / 100)}
              `}
              dur="3.2s"
              repeatCount="indefinite"
            />
          </path>
        </g>

        <rect
          x="100"
          y="80"
          width="400"
          height="340"
          rx="8"
          fill="url(#glassShine)"
          stroke="#64748b"
          strokeWidth="2"
        />

        <rect
          x="102"
          y="82"
          width="396"
          height="336"
          rx="7"
          fill="none"
          stroke="#cbd5e1"
          strokeWidth="1"
          opacity="0.4"
        />

        {children}
      </svg>
    </div>
  );
};
